import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import api from "../../api"; // axios instance with token + school_id interceptor

const DepartmentPage = () => {
    const [form, setForm] = useState({ name: "", code: "" });
    const [items, setItems] = useState([]);
    const [editId, setEditId] = useState(null);
    const [msg, setMsg] = useState(null);

    const baseURL = "/departments";
    const schoolId = localStorage.getItem("school_id");

    // fetch departments
    const fetchDepartments = async () => {
        try {
            const res = await api.get(`${baseURL}/list?school_id=${schoolId}`);
            const data = res.data;
            const list = data?.data ?? [];
            setItems(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error("Error fetching departments:", err);
            setItems([]);
        }
    };

    useEffect(() => {
        fetchDepartments();
    }, []);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMsg(null);

        if (!form.name || form.name.trim() === "") {
            setMsg({ type: "error", text: "Department name required" });
            return;
        }

        const payload = { ...form };

        try {
            let res;
            if (editId) {
                res = await api.post(`${baseURL}/update`, { ...payload, id: editId });
            } else {
                res = await api.post(`${baseURL}/add`, payload);
            }

            const json = res.data;

            if (json?.status) {
                setMsg({
                    type: "success",
                    text: json.message || (editId ? "Updated successfully" : "Added successfully"),
                });
            } else {
                setMsg({ type: "error", text: json?.message || "Operation failed" });
            }

            setForm({ name: "", code: "" });
            setEditId(null);
            fetchDepartments();
        } catch (err) {
            console.error("Error saving department:", err);
            setMsg({
                type: "error",
                text: err.response?.data?.message || err.message || "Network error",
            });
        }
    };

    const handleEdit = (item) => {
        setForm({ name: item.name ?? "", code: item.code ?? "", category:item.category ?? "", section: item.section ?? "" });
        setEditId(item.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this department?")) return;
        try {
            const res = await api.post(`${baseURL}/delete`, { id });
            const json = res.data;
            if (json?.status) {
                setMsg({ type: "success", text: json.message || "Deleted" });
            } else {
                setMsg({ type: "error", text: json?.message || "Delete failed" });
            }
            fetchDepartments();
        } catch (err) {
            console.error("Delete error", err);
            setMsg({
                type: "error",
                text: err.response?.data?.message || err.message || "Network error",
            });
        }
    };

    return (
        <div style={{ display: "flex", minHeight: "100vh" }}>
            <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
                <main
                    style={{
                        padding: "30px",
                        maxWidth: "1000px",
                        margin: "auto",
                        marginTop: "0px",
                    }}
                >
                    {/* Add/Edit Department Section */}
                    <section style={cardStyle}>
                        <h2 style={titleStyle}>
                            {editId ? "✏️ Edit Department" : "➕ Add New Department"}
                        </h2>

                        {msg && (
                            <div
                                style={{
                                    marginBottom: 12,
                                    color: msg.type === "error" ? "#cc0000" : "#166534",
                                }}
                            >
                                {msg.text}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div style={formGrid}>
                                <FormGroup label="Department Name">
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        style={fullWidthInput}
                                        required
                                    />
                                </FormGroup>

                                <FormGroup label="Code (optional)">
                                    <input
                                        type="text"
                                        name="code"
                                        value={form.code}
                                        onChange={handleChange}
                                        style={fullWidthInput}
                                    />
                                </FormGroup>

                                <FormGroup label="Category">
                                    <select
                                        name="category"
                                        value={form.category}
                                        onChange={handleChange}
                                        style={fullWidthInput}
                                    >
                                        <option value="">Select</option>
                                        <option value="Teaching">Teaching</option>
                                        <option value="Non-Teaching">Non-Teaching</option>
                                    </select>
                                </FormGroup>

                                {form.category === "Teaching" && (
                                    <FormGroup label="Section (e.g. Baby Land, Upper)">
                                        <input
                                            type="text"
                                            name="section"
                                            value={form.section || ""}
                                            onChange={handleChange}
                                            style={fullWidthInput}
                                            placeholder="Enter section name"
                                        />
                                    </FormGroup>
                                )}

                            </div>

                            <div style={{ textAlign: "right", marginTop: "20px" }}>
                                <button type="submit" style={buttonStyle}>
                                    {editId ? "Update Department" : "Add Department"}
                                </button>
                            </div>
                        </form>
                    </section>

                    {/* List Departments */}
                    <section style={cardStyle}>
                        <h2 style={titleStyle}>📋 All Departments</h2>
                        {items.length === 0 ? (
                            <p>No departments found.</p>
                        ) : (
                            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                {items.map((item, idx) => (
                                    <li
                                        key={item.id}
                                        style={{
                                            marginBottom: "15px",
                                            padding: "15px",
                                            borderRadius: "8px",
                                            background: idx % 2 === 0 ? "#f9f9f9" : "#eef3f9",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div>
                                            <strong style={{ fontSize: "16px", color: "#333" }}>{item.name}</strong>
                                            <div style={{ color: "#777", fontSize: "13px" }}>
                                                {item.category}{" "}
                                                {item.section ? `| Section: ${item.section}` : ""}{" "}
                                                {item.code ? `| Code: ${item.code}` : ""}{" "}
                                                {item.created_at ? `| Created: ${item.created_at}` : ""}
                                            </div>
                                        </div>
                                        <div>
                                            <FaEdit
                                                onClick={() => handleEdit(item)}
                                                style={{ color: "#0077cc", cursor: "pointer", marginRight: "15px" }}
                                            />
                                            <FaTrash
                                                onClick={() => handleDelete(item.id)}
                                                style={{ color: "red", cursor: "pointer" }}
                                            />
                                        </div>
                                    </li>
                                ))}
                            </ul>

                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

// Reusable Form Group
const formGrid = {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "20px",
};

const fullWidthInput = {
    width: "100%",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "15px",
    boxSizing: "border-box",
};

const FormGroup = ({ label, children, style }) => (
    <div style={{ ...style }}>
        <label
            style={{
                marginBottom: "6px",
                display: "block",
                fontWeight: 500,
                color: "#444",
            }}
        >
            {label}
        </label>
        {children}
    </div>
);

const buttonStyle = {
    padding: "10px 25px",
    backgroundColor: "#0077cc",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "15px",
    cursor: "pointer",
};

const cardStyle = {
    background: "#ffffff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
    marginBottom: "30px",
};

const titleStyle = {
    color: "#0077cc",
    marginBottom: "25px",
};

export default DepartmentPage;
